/**
 * Maps a plain-text offset (from rendered markdown via Range.toString()) to the
 * corresponding position in the raw markdown source.
 *
 * Rendered markdown strips syntax characters (heading markers, emphasis markers,
 * link brackets/URLs, list markers, etc.). This function walks the raw source,
 * skipping those invisible characters so the returned index points to the same
 * logical position the user clicked on.
 */
export function mapPlainOffsetToRaw(
  raw: string,
  targetPlainOffset: number
): number {
  let ri = 0;
  let pi = 0;
  const len = raw.length;

  while (ri < len && pi < targetPlainOffset) {
    const atLineStart = ri === 0 || raw[ri - 1] === '\n';

    // Paragraph break: \n\n+ renders as a single line break in Range.toString()
    if (raw[ri] === '\n' && ri + 1 < len && raw[ri + 1] === '\n') {
      while (ri < len && raw[ri] === '\n') ri++;
      pi++;
      continue;
    }

    // Block-level syntax (only at start of line)
    if (atLineStart) {
      // Heading: # to ######
      if (raw[ri] === '#') {
        let n = 0;
        while (ri + n < len && raw[ri + n] === '#') n++;
        if (n <= 6 && ri + n < len && raw[ri + n] === ' ') {
          ri += n + 1;
          continue;
        }
      }
      // Unordered list: - , * , +
      if (
        (raw[ri] === '-' || raw[ri] === '*' || raw[ri] === '+') &&
        ri + 1 < len &&
        raw[ri + 1] === ' '
      ) {
        ri += 2;
        continue;
      }
      // Ordered list: 1. , 12. etc.
      let numLen = 0;
      while (
        ri + numLen < len &&
        raw[ri + numLen] >= '0' &&
        raw[ri + numLen] <= '9'
      )
        numLen++;
      if (
        numLen > 0 &&
        ri + numLen + 1 < len &&
        raw[ri + numLen] === '.' &&
        raw[ri + numLen + 1] === ' '
      ) {
        ri += numLen + 2;
        continue;
      }
      // Blockquote: >
      if (raw[ri] === '>' && ri + 1 < len && raw[ri + 1] === ' ') {
        ri += 2;
        continue;
      }
    }

    // Emphasis: * ** *** _ __ ___
    if (raw[ri] === '*' || raw[ri] === '_') {
      const ch = raw[ri];
      let n = 0;
      while (ri + n < len && raw[ri + n] === ch) n++;
      if (n <= 3) {
        ri += n;
        continue;
      }
    }
    // Strikethrough: ~~
    if (raw[ri] === '~' && ri + 1 < len && raw[ri + 1] === '~') {
      ri += 2;
      continue;
    }
    // Inline code backtick
    if (raw[ri] === '`') {
      ri++;
      continue;
    }
    // Image prefix: ![
    if (raw[ri] === '!' && ri + 1 < len && raw[ri + 1] === '[') {
      ri++;
      continue;
    }
    // Link opening bracket: [
    if (raw[ri] === '[') {
      ri++;
      continue;
    }
    // Link URL part: ](url)
    if (raw[ri] === ']' && ri + 1 < len && raw[ri + 1] === '(') {
      ri += 2;
      while (ri < len && raw[ri] !== ')') ri++;
      if (ri < len) ri++;
      continue;
    }

    // Regular character
    ri++;
    pi++;
  }

  return Math.min(ri, len);
}
